import React, { useEffect, useState, useRef } from "react";
import { api, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import UserAvatar from "@/components/UserAvatar";
import { Camera, User as UserIcon, Phone, Envelope, Buildings, ShieldCheck, Eye, EyeSlash } from "@phosphor-icons/react";

const TITLE_SUGGESTIONS = ["Ing.", "Dott.", "Dott.ssa", "Avv.", "Arch.", "Geom.", "Dr.", "Dr.ssa", "Prof.", "Notaio", "Rag.", "P.I."];

const ROLE_COLORS = {
  admin: "#991B1B",
  desk_operator: "#4F46E5",
  site_operator: "#166534",
  tech_operator: "#CA8A04",
  accounting_operator: "#6D28D9",
  professional: "#0369A1",
};

// Iter209au — ROLE_LABELS ora importato da @/lib/api (single source).

export default function Profile({ embedded = false }) {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    email: user?.email || "",
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    title: user?.title || "",
    phone: user?.phone || "",
    company_name: user?.company_name || "",
  });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [showPwd, setShowPwd] = useState({ current: false, next: false, confirm: false });
  const [operators, setOperators] = useState([]);
  const [saving, setSaving] = useState(false);
  const avatarRef = useRef();

  useEffect(() => {
    api.get("/directory/operators").then(r => setOperators(r.data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.patch("/auth/profile", form);
      setUser(r.data);
      localStorage.setItem("ht_user", JSON.stringify(r.data));
      toast.success("Profilo aggiornato");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const isProfessional = user?.role === "professional";
  const photoLabel = isProfessional ? "logo" : "foto profilo";
  const photoLabelCap = photoLabel.charAt(0).toUpperCase() + photoLabel.slice(1);

  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/auth/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const me = await api.get("/auth/me");
      setUser(me.data);
      localStorage.setItem("ht_user", JSON.stringify(me.data));
      toast.success(`${photoLabelCap} aggiornato`);
    } catch (err) { toast.error("Errore upload"); }
    e.target.value = "";
  };

  const changePwd = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) {
      toast.error("Le password non corrispondono");
      return;
    }
    try {
      await api.post("/auth/password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Password aggiornata");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const initials = ((user?.first_name?.[0] || "") + (user?.last_name?.[0] || "")).toUpperCase() || user?.email?.[0]?.toUpperCase();
  const avatarSrc = user?.avatar_url ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_signed_url || user.avatar_url}` : null;
  const displayName = [user?.title, user?.first_name, user?.last_name].filter(Boolean).join(" ");

  return (
    <div>
      <div className="mb-8">
        <div className="label-eyebrow">Il tuo profilo</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Impostazioni personali</h1>
        <p className="text-muted-foreground mt-2">Gestisci i tuoi dati e visualizza il team operativo</p>
      </div>

      <Tabs defaultValue="profilo">
        <TabsList>
          <TabsTrigger value="profilo" data-testid="tab-profile">Il mio profilo</TabsTrigger>
          <TabsTrigger value="password" data-testid="tab-password">Password</TabsTrigger>
          {user?.role !== "professional" && (
            <TabsTrigger value="team" data-testid="tab-team">Team operativo ({operators.length})</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="profilo">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Avatar card */}
            <div className="kpi-card text-center">
              <div className="relative mx-auto w-32 h-32 mb-4">
                <UserAvatar user={user} size={128} useRoleGradient testId="profile-avatar" />
                <button onClick={() => avatarRef.current?.click()}
                  className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-white border-2 border-purple-600 flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
                  data-testid="upload-avatar-btn">
                  <Camera size={16} color="#6D28D9" weight="bold" />
                </button>
                <input ref={avatarRef} type="file" hidden accept="image/*" onChange={uploadAvatar} data-testid="avatar-input" />
              </div>
              <div className="font-display text-lg font-semibold">{displayName || user?.email}</div>
              <span className="role-badge mt-2 inline-block" style={{ background: `${ROLE_COLORS[user?.role]}20`, color: ROLE_COLORS[user?.role] }}>
                {ROLE_LABELS[user?.role]}
              </span>
              {user?.is_supervisor && (
                <div className="mt-2 flex items-center justify-center gap-1 text-xs text-purple-700 font-semibold">
                  <ShieldCheck size={12} weight="fill" /> Supervisor
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-3">Clicca l'icona fotocamera per cambiare il {photoLabel}</p>
            </div>

            {/* Form */}
            <form onSubmit={save} className="md:col-span-2 kpi-card space-y-4" data-testid="profile-form">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>Titolo</Label>
                  <Input value={form.title} onChange={upd("title")} placeholder="es. Ing., Dott., Arch." data-testid="profile-title" list="title-suggestions" />
                  <datalist id="title-suggestions">
                    {TITLE_SUGGESTIONS.map(t => <option key={t} value={t} />)}
                  </datalist>
                </div>
                <div>
                  <Label>Nome</Label>
                  <Input required value={form.first_name} onChange={upd("first_name")} data-testid="profile-first-name" />
                </div>
                <div>
                  <Label>Cognome</Label>
                  <Input required value={form.last_name} onChange={upd("last_name")} data-testid="profile-last-name" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="flex items-center gap-1"><Envelope size={12} /> Email</Label>
                  <Input type="email" required value={form.email} onChange={upd("email")} data-testid="profile-email" />
                </div>
                <div>
                  <Label className="flex items-center gap-1"><Phone size={12} /> Telefono</Label>
                  <Input type="tel" value={form.phone} onChange={upd("phone")} placeholder="+39 333 1234567" data-testid="profile-phone" />
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-1"><Buildings size={12} /> Ragione sociale / Studio</Label>
                <Input value={form.company_name} onChange={upd("company_name")} data-testid="profile-company" />
              </div>

              <div className="pt-3">
                <Button type="submit" disabled={saving} className="ht-gradient border-0" data-testid="save-profile">
                  {saving ? "Salvataggio..." : "Salva modifiche"}
                </Button>
              </div>
            </form>
          </div>

          {/* Iter208.57 — Sezione Dati Studio (solo PRO Master) */}
          {user?.role === "professional" && !user?.parent_user_id && (
            <div className="mt-6">
              <StudioDataCard user={user} setUser={setUser} />
            </div>
          )}

          {/* Firma per PDF preventivi — solo PRO Master (i collaboratori usano la firma del Master) */}
          {user?.role === "professional" && !user?.parent_user_id && (
            <div className="mt-6">
              <SignatureCard user={user} setUser={setUser} />
            </div>
          )}

          {/* GDPR — Privacy & Dati */}
          <div className="mt-6">
            <GdprPanel user={user} />
          </div>
        </TabsContent>

        <TabsContent value="password">
          {user?.auth_provider === "google" ? (
            <div
              className="kpi-card max-w-md flex items-start gap-3"
              data-testid="password-google-banner"
            >
              <svg viewBox="0 0 24 24" width={22} height={22} className="mt-0.5 shrink-0" aria-hidden="true">
                <path fill="#4285F4" d="M23.49 12.27c0-.86-.08-1.68-.22-2.47H12v4.68h6.44c-.28 1.5-1.13 2.77-2.4 3.62v3.01h3.88c2.27-2.09 3.57-5.17 3.57-8.84z" />
                <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.94-2.92l-3.88-3.01c-1.08.72-2.45 1.15-4.06 1.15-3.13 0-5.78-2.11-6.73-4.96H1.28v3.11C3.26 21.3 7.31 24 12 24z" />
                <path fill="#FBBC05" d="M5.27 14.26c-.24-.72-.38-1.49-.38-2.26s.14-1.54.38-2.26V6.63H1.28C.46 8.24 0 10.06 0 12s.46 3.76 1.28 5.37l3.99-3.11z" />
                <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.94 1.15 15.23 0 12 0 7.31 0 3.26 2.7 1.28 6.63l3.99 3.11C6.22 6.86 8.87 4.75 12 4.75z" />
              </svg>
              <div className="text-sm text-slate-800">
                <div className="font-semibold text-slate-900 mb-1">Account gestito da Google</div>
                <p className="text-slate-700 leading-relaxed">
                  Hai effettuato la registrazione tramite Google, quindi non hai una password interna Housetag. Per modificare la password del tuo account gestisci le credenziali direttamente su Google.
                </p>
                <a
                  href="https://myaccount.google.com/security"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 text-purple-700 hover:text-purple-900 hover:underline text-sm font-semibold"
                  data-testid="password-google-link"
                >
                  Vai a Google Account Security
                  <span aria-hidden="true">↗</span>
                </a>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Suggerimento: per un accesso ancora più rapido, aggiungi una <strong>passkey</strong> dalla tab <em>Sicurezza</em> — Face ID / Touch ID sostituiscono la password.
                </p>
              </div>
            </div>
          ) : (
          <form onSubmit={changePwd} className="kpi-card max-w-md space-y-4" data-testid="password-form">
            <h3 className="font-display text-xl font-semibold">Modifica password</h3>
            <div>
              <Label>Password attuale</Label>
              <div className="relative">
                <Input type={showPwd.current ? "text" : "password"} required value={pwd.current_password}
                  onChange={e => setPwd({ ...pwd, current_password: e.target.value })}
                  className="pr-10" data-testid="pwd-current" />
                <button type="button" onClick={() => setShowPwd(s => ({ ...s, current: !s.current }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                  aria-label={showPwd.current ? "Nascondi" : "Mostra"}
                  data-testid="toggle-pwd-current">
                  {showPwd.current ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Nuova password (min 6 caratteri)</Label>
              <div className="relative">
                <Input type={showPwd.next ? "text" : "password"} required minLength={6} value={pwd.new_password}
                  onChange={e => setPwd({ ...pwd, new_password: e.target.value })}
                  className="pr-10" data-testid="pwd-new" />
                <button type="button" onClick={() => setShowPwd(s => ({ ...s, next: !s.next }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                  aria-label={showPwd.next ? "Nascondi" : "Mostra"}
                  data-testid="toggle-pwd-new">
                  {showPwd.next ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Conferma nuova password</Label>
              <div className="relative">
                <Input type={showPwd.confirm ? "text" : "password"} required value={pwd.confirm}
                  onChange={e => setPwd({ ...pwd, confirm: e.target.value })}
                  className="pr-10" data-testid="pwd-confirm" />
                <button type="button" onClick={() => setShowPwd(s => ({ ...s, confirm: !s.confirm }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                  aria-label={showPwd.confirm ? "Nascondi" : "Mostra"}
                  data-testid="toggle-pwd-confirm">
                  {showPwd.confirm ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="ht-gradient border-0" data-testid="save-pwd">Aggiorna password</Button>
          </form>
          )}
        </TabsContent>

        <TabsContent value="team">
          <div className="kpi-card">
            <p className="text-sm text-muted-foreground mb-4">
              {user?.role === "professional"
                ? "Sotto-utenti collegati al tuo account. Puoi invitare colleghi del tuo studio: l'invito viene inviato via email e attivato automaticamente se il tuo account è approvato."
                : "Contatti e badge di tutti gli operatori attivi della piattaforma."}
            </p>
            {operators.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                {user?.role === "professional" ? "Nessun collaboratore invitato ancora." : "Nessun operatore trovato"}
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-grid">
                {operators.map(op => {
                  const opName = [op.title, op.first_name, op.last_name].filter(Boolean).join(" ");
                  return (
                    <div key={op.id} className="flex items-start gap-3 p-3 border rounded-lg hover:border-purple-300 hover:shadow-sm transition-all" data-testid={`op-card-${op.id}`}>
                      <UserAvatar user={op} size={56} testId={`op-avatar-${op.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{opName || op.email}</div>
                        <div className="text-xs" style={{ color: ROLE_COLORS[op.role] }}>
                          {ROLE_LABELS[op.role]}
                          {op.is_supervisor && " · Supervisor"}
                        </div>
                        <div className="text-xs text-muted-foreground truncate mt-1">{op.email}</div>
                        {op.phone && <div className="text-xs text-muted-foreground flex items-center gap-1"><Phone size={10} /> {op.phone}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}


// ==================== SignatureCard ====================
function SignatureCard({ user, setUser }) {
  const inputRef = React.useRef();
  const [uploading, setUploading] = React.useState(false);
  const sigSrc = user?.signature_url ? `${process.env.REACT_APP_BACKEND_URL}${user.signature_signed_url || user.signature_url}` : null;

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/auth/signature", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const me = await api.get("/auth/me");
      setUser(me.data);
      localStorage.setItem("ht_user", JSON.stringify(me.data));
      toast.success("Firma caricata — comparirà in tutti i PDF preventivo");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload firma");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("Rimuovere la firma?")) return;
    try {
      await api.delete("/auth/signature");
      const me = await api.get("/auth/me");
      setUser(me.data);
      localStorage.setItem("ht_user", JSON.stringify(me.data));
      toast.success("Firma rimossa");
    } catch (_) { toast.error("Errore"); }
  };

  return (
    <div className="kpi-card" data-testid="signature-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-semibold">Logo / Insegna dello studio</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-lg">
            Carica il logo o l'insegna del tuo studio (PNG con sfondo trasparente consigliato).
            Verrà apposto sui preventivi e documenti generati dalla piattaforma.
          </p>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-4 flex-wrap">
        <div className="w-56 h-24 rounded-xl border-2 border-dashed border-black/15 bg-white flex items-center justify-center overflow-hidden" data-testid="signature-preview">
          {sigSrc ? (
            <img src={sigSrc} alt="logo studio" className="max-w-full max-h-full object-contain" />
          ) : (
            <span className="text-xs text-black/40">Nessun logo caricato</span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={inputRef} type="file" hidden accept="image/png,image/jpeg,image/webp" onChange={upload} data-testid="signature-input" />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="px-4 py-2 rounded-lg bg-black text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
            data-testid="signature-upload-btn"
          >
            {uploading ? "Carico..." : sigSrc ? "Sostituisci logo" : "Carica logo"}
          </button>
          {sigSrc && (
            <button
              onClick={remove}
              className="px-4 py-2 rounded-lg border border-black/15 text-sm hover:bg-black/5"
              data-testid="signature-remove-btn"
            >
              Rimuovi
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== StudioDataCard (Iter208.57) ====================
// Sezione "Dati Studio" nella tab "Il mio profilo": mostra e permette di
// modificare i campi anagrafici dello studio inseriti in fase di registrazione:
// Partita IVA, indirizzo sede legale, PEC, codice SDI.
// Iter209aw — Il campo "Tipo di studio" è stato rimosso.
function StudioDataCard({ user, setUser }) {
  const [f, setF] = React.useState({
    company_vat: user?.company_vat || "",
    company_toponimo: user?.company_toponimo || "",
    company_address: user?.company_address || "",
    company_civico: user?.company_civico || "",
    company_city: user?.company_city || "",
    company_pec: user?.company_pec || "",
    company_sdi: user?.company_sdi || "",
  });
  const [saving, setSaving] = React.useState(false);
  const upd = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/auth/profile", f);
      setUser(r.data);
      localStorage.setItem("ht_user", JSON.stringify(r.data));
      toast.success("Dati Studio aggiornati");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="kpi-card" data-testid="studio-data-card">
      <div className="flex items-start gap-2">
        <Buildings size={18} weight="duotone" className="text-purple-600 mt-0.5" />
        <div>
          <h3 className="font-display text-lg font-semibold">Dati Studio</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Anagrafica fiscale e sede legale dello studio, utilizzata per preventivi, fatture e comunicazioni ufficiali.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {/* Iter209aw — Campo "Tipo di studio" rimosso completamente
            (form, backend, validazione, deps.pub_user). La colonna
            eventualmente presente in DB su utenti esistenti viene
            ignorata: non è più letta né scritta. */}
        <div>
          <Label className="text-xs">Partita IVA</Label>
          <Input
            value={f.company_vat}
            onChange={upd("company_vat")}
            maxLength={16}
            placeholder="IT12345678901"
            data-testid="studio-vat-input"
          />
        </div>
        <div>
          <Label className="text-xs">PEC</Label>
          <Input
            type="email"
            value={f.company_pec}
            onChange={upd("company_pec")}
            placeholder="studio@pec.it"
            data-testid="studio-pec-input"
          />
        </div>
        <div>
          <Label className="text-xs">Codice SDI</Label>
          <Input
            value={f.company_sdi}
            onChange={upd("company_sdi")}
            maxLength={7}
            placeholder="Es. SUBM70N (7 caratteri)"
            data-testid="studio-sdi-input"
          />
        </div>

        <div className="md:col-span-2 border-t pt-3">
          <div className="text-xs font-semibold text-slate-600 mb-2">Sede legale</div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
            <div className="md:col-span-3">
              <Label className="text-xs">Toponimo</Label>
              <select
                value={f.company_toponimo}
                onChange={upd("company_toponimo")}
                className="mt-1 w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="studio-toponimo-select"
              >
                <option value="">—</option>
                <option>Via</option>
                <option>Viale</option>
                <option>Corso</option>
                <option>Piazza</option>
                <option>Piazzale</option>
                <option>Largo</option>
                <option>Vicolo</option>
                <option>Strada</option>
              </select>
            </div>
            <div className="md:col-span-6">
              <Label className="text-xs">Indirizzo</Label>
              <Input
                value={f.company_address}
                onChange={upd("company_address")}
                placeholder="Es. Roma"
                data-testid="studio-address-input"
              />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs">Civico</Label>
              <Input
                value={f.company_civico}
                onChange={upd("company_civico")}
                placeholder="12/A"
                data-testid="studio-civico-input"
              />
            </div>
            <div className="md:col-span-12">
              <Label className="text-xs">Città</Label>
              <Input
                value={f.company_city}
                onChange={upd("company_city")}
                placeholder="Milano"
                data-testid="studio-city-input"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="pt-4">
        <Button
          onClick={save}
          disabled={saving}
          className="ht-gradient border-0"
          data-testid="save-studio-data"
        >
          {saving ? "Salvataggio…" : "Salva modifiche"}
        </Button>
      </div>
    </div>
  );
}



// ==================== GDPR Panel ====================
function GdprPanel({ user }) {
  const [downloading, setDownloading] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [pwd, setPwd] = React.useState("");
  const [confirmText, setConfirmText] = React.useState("");
  const [deleting, setDeleting] = React.useState(false);
  // Modal "blocked by practices" quando il backend ritorna 400 con code=blocked_by_practices
  const [blocked, setBlocked] = React.useState(null); // { message, in_progress[], pending_payment[] }
  const [supportNote, setSupportNote] = React.useState("");
  const [supportSending, setSupportSending] = React.useState(false);
  const [supportSent, setSupportSent] = React.useState(false);
  const { logout } = useAuth();

  const downloadExport = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/users/me/export-data", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `housetag_export_${user?.id?.slice(0, 8) || "me"}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export scaricato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore export dati");
    } finally { setDownloading(false); }
  };

  const doDelete = async () => {
    if (confirmText !== "ELIMINA") {
      toast.error("Digita ELIMINA per confermare");
      return;
    }
    setDeleting(true);
    try {
      await api.delete("/users/me/delete-account", { data: { password: pwd, confirm: confirmText } });
      toast.success("Account eliminato. I dati personali sono stati anonimizzati.");
      await logout();
      window.location.href = "/";
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Backend ritorna detail = { code: 'blocked_by_practices', message, in_progress, pending_payment }
      if (detail && typeof detail === "object" && detail.code === "blocked_by_practices") {
        setBlocked({
          message: detail.message,
          in_progress: detail.in_progress || [],
          pending_payment: detail.pending_payment || [],
        });
        setSupportSent(false);
        setSupportNote("");
      } else {
        toast.error(typeof detail === "string" ? detail : "Errore eliminazione");
      }
    } finally { setDeleting(false); }
  };

  const contactSupport = async () => {
    setSupportSending(true);
    try {
      await api.post("/users/me/contact-support/account-deletion", { note: supportNote });
      setSupportSent(true);
      toast.success("Richiesta inviata al supporto. Ti ricontatteremo via email.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio richiesta");
    } finally { setSupportSending(false); }
  };

  const isAdmin = user?.role === "admin";

  return (
    <div className="kpi-card" data-testid="gdpr-panel">
      <h3 className="font-display text-lg font-semibold">Privacy e dati personali (GDPR)</h3>
      <p className="text-sm text-muted-foreground mt-1">
        In linea con il Regolamento UE 2016/679, hai diritto ad accedere ai tuoi dati,
        esportarli e chiederne la cancellazione.
      </p>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Export */}
        <div className="rounded-xl border border-black/10 bg-white p-4">
          <div className="font-semibold text-sm">Esporta i miei dati</div>
          <p className="text-xs text-muted-foreground mt-1">
            Scarica un file JSON con profilo, immobili, pratiche, preventivi e log di accesso.
          </p>
          <button
            onClick={downloadExport}
            disabled={downloading}
            className="mt-3 w-full px-4 py-2 rounded-lg bg-black text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
            data-testid="gdpr-export-btn"
          >
            {downloading ? "Genero..." : "Scarica archivio JSON"}
          </button>
        </div>

        {/* Delete */}
        <div className="rounded-xl border border-red-200 bg-red-50/50 p-4">
          <div className="font-semibold text-sm text-red-800">Elimina il mio account</div>
          <p className="text-xs text-red-700/80 mt-1">
            Anonimizza definitivamente i tuoi dati personali. Le pratiche e i documenti
            fiscali già emessi vengono conservati in forma anonima per obblighi di legge.
          </p>
          {isAdmin ? (
            <div className="mt-3 text-xs text-red-700 font-semibold">
              Gli account amministratore non possono auto-eliminarsi.
            </div>
          ) : !deleteOpen ? (
            <button
              onClick={() => setDeleteOpen(true)}
              className="mt-3 w-full px-4 py-2 rounded-lg border border-red-300 text-red-800 text-sm font-medium hover:bg-red-100"
              data-testid="gdpr-delete-open-btn"
            >
              Elimina account…
            </button>
          ) : (
            <div className="mt-3 space-y-2">
              {user?.auth_provider === "local" && (
                <div>
                  <Label className="text-xs text-red-800">Conferma password</Label>
                  <Input
                    type="password"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    data-testid="gdpr-delete-pwd"
                    className="text-sm"
                  />
                </div>
              )}
              <div>
                <Label className="text-xs text-red-800">Digita ELIMINA per confermare</Label>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="ELIMINA"
                  data-testid="gdpr-delete-confirm-input"
                  className="text-sm"
                />
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={doDelete}
                  disabled={deleting || confirmText !== "ELIMINA"}
                  className="flex-1 px-3 py-2 rounded-lg bg-red-700 text-white text-sm font-semibold hover:bg-red-800 disabled:opacity-50"
                  data-testid="gdpr-delete-confirm-btn"
                >
                  {deleting ? "..." : "Elimina definitivamente"}
                </button>
                <button
                  onClick={() => { setDeleteOpen(false); setPwd(""); setConfirmText(""); }}
                  className="flex-1 px-3 py-2 rounded-lg border border-red-300 text-red-800 text-sm hover:bg-red-100"
                  data-testid="gdpr-delete-cancel-btn"
                >
                  Annulla
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal: eliminazione bloccata da pratiche in corso o pagamenti in sospeso */}
      {blocked && (
        <div
          className="fixed inset-0 z-[1000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="gdpr-delete-blocked-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-5 py-4 border-b border-red-100 bg-red-50 flex items-start justify-between gap-3">
              <div>
                <div className="text-base font-semibold text-red-800">Eliminazione account bloccata</div>
                <div className="text-xs text-red-700/80 mt-1">{blocked.message}</div>
              </div>
              <button
                onClick={() => setBlocked(null)}
                className="text-red-700 hover:text-red-900 text-sm shrink-0 px-2 py-1"
                data-testid="gdpr-blocked-close-btn"
                aria-label="Chiudi"
              >
                ✕
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto space-y-5">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-2">
                  Servizi in corso ({blocked.in_progress.length})
                </div>
                {blocked.in_progress.length === 0 ? (
                  <div className="text-xs text-muted-foreground italic">Nessuno</div>
                ) : (
                  <ul className="divide-y divide-amber-100 rounded-lg border border-amber-200 bg-amber-50/30" data-testid="gdpr-blocked-in-progress-list">
                    {blocked.in_progress.map((p) => (
                      <li key={p.id} className="px-3 py-2 text-sm flex items-center justify-between gap-3" data-testid={`gdpr-blocked-in-progress-item-${p.id}`}>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{p.title}</div>
                          <div className="text-[11px] text-muted-foreground font-mono truncate">{p.id}</div>
                        </div>
                        <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-100 px-2 py-0.5 rounded">
                          {p.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-red-700 mb-2">
                  Pagamenti in sospeso ({blocked.pending_payment.length})
                </div>
                {blocked.pending_payment.length === 0 ? (
                  <div className="text-xs text-muted-foreground italic">Nessuno</div>
                ) : (
                  <ul className="divide-y divide-red-100 rounded-lg border border-red-200 bg-red-50/30" data-testid="gdpr-blocked-pending-payment-list">
                    {blocked.pending_payment.map((p) => (
                      <li key={p.id} className="px-3 py-2 text-sm flex items-center justify-between gap-3" data-testid={`gdpr-blocked-pending-payment-item-${p.id}`}>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{p.title}</div>
                          <div className="text-[11px] text-muted-foreground font-mono truncate">{p.id}</div>
                        </div>
                        <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-red-800 bg-red-100 px-2 py-0.5 rounded">
                          {p.payment_status || p.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {!supportSent && (
                <div>
                  <Label className="text-xs text-muted-foreground">Nota per il supporto (facoltativa)</Label>
                  <textarea
                    value={supportNote}
                    onChange={(e) => setSupportNote(e.target.value)}
                    rows={3}
                    maxLength={1000}
                    placeholder="Specifica qui eventuali richieste o dettagli utili al supporto…"
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-black/10 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                    data-testid="gdpr-blocked-support-note"
                  />
                </div>
              )}

              {supportSent && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="gdpr-blocked-support-sent">
                  Richiesta inviata al supporto. Ti ricontatteremo via email per concludere il rapporto.
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-black/5 bg-gray-50 flex items-center justify-end gap-2 shrink-0">
              <button
                onClick={() => setBlocked(null)}
                className="px-4 py-2 rounded-lg border border-black/10 text-sm hover:bg-white"
                data-testid="gdpr-blocked-dismiss-btn"
              >
                Chiudi
              </button>
              {!supportSent && (
                <button
                  onClick={contactSupport}
                  disabled={supportSending}
                  className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50"
                  data-testid="gdpr-blocked-contact-support-btn"
                >
                  {supportSending ? "Invio…" : "Contatta il supporto"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

